import { BrowserSessionApiClient } from "./browserSessionApiClient";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/+$/, "");

export type FeedbackReportType = "bug" | "suggestion";
export type FeedbackHandlingStatus = "new" | "in_progress" | "resolved" | "verified" | "ignored";

export type FeedbackReportSubmitInput = {
  reportType: FeedbackReportType;
  description: string;
  routePath: string;
  pageTitle?: string | null;
  capturedAt?: string | null;
  screenshotBase64?: string | null;
  screenshotContentType?: string | null;
  screenshotFileName?: string | null;
  clientContext?: {
    userAgent?: string | null;
    viewportWidth?: number | null;
    viewportHeight?: number | null;
    pageUrl?: string | null;
  } | null;
};

export type FeedbackReportSubmitResult = {
  reportId: string;
  status: "submitted" | "duplicate";
  deduplicated: boolean;
  duplicateOfReportId: string | null;
  analysisStatus: "disabled" | "pending" | "completed" | "failed" | "skipped";
  submittedAt: string;
  localDate: string;
  artifactPaths: {
    reportFolderPath: string;
    submissionPath: string | null;
    screenshotPath: string | null;
    analysisPath: string | null;
    handlingPath: string | null;
  };
};

export type FeedbackHandlingUpdateInput = {
  localDate: string;
  reportId: string;
  handlingStatus: FeedbackHandlingStatus;
  note?: string | null;
};

export type FeedbackHandlingUpdateResult = {
  reportId: string;
  localDate: string;
  handlingStatus: FeedbackHandlingStatus;
  updatedAt: string;
  note: string | null;
  historyCount: number;
  artifactPaths: {
    handlingPath: string;
  };
};

export type FeedbackDailySummary = {
  localDate: string;
  timeZone: string;
  counts: {
    total: number;
    bugs: number;
    suggestions: number;
    duplicates: number;
  };
  analysisStatusSummary: Record<"disabled" | "pending" | "completed" | "failed" | "skipped", number>;
  bugs: Array<Record<string, unknown>>;
  suggestions: Array<Record<string, unknown>>;
  duplicates: Array<Record<string, unknown>>;
  emailSummary: {
    sent: boolean;
    sentAt: string | null;
    recipients: string[];
    subject: string | null;
  };
};

function unwrapData<T>(value: any): T {
  if (value && typeof value === "object" && "data" in value) {
    return value.data as T;
  }
  return value as T;
}

class FeedbackReportService {
  private readonly browserClient = new BrowserSessionApiClient(API_BASE_URL);

  async submitReport(input: FeedbackReportSubmitInput): Promise<FeedbackReportSubmitResult> {
    const response = await this.browserClient.request(`${API_BASE_URL}/v1/feedback-reports`, {
      method: "POST",
      body: input,
    });
    return unwrapData<FeedbackReportSubmitResult>(response);
  }

  async getDailySummary(date?: string): Promise<FeedbackDailySummary> {
    const response = await this.browserClient.request(`${API_BASE_URL}/v1/feedback-reports/daily`, {
      method: "GET",
      query: date ? { date } : {},
    });
    return unwrapData<FeedbackDailySummary>(response);
  }

  async updateHandling(input: FeedbackHandlingUpdateInput): Promise<FeedbackHandlingUpdateResult> {
    const response = await this.browserClient.request(`${API_BASE_URL}/v1/feedback-reports/handling`, {
      method: "PATCH",
      body: input,
    });
    return unwrapData<FeedbackHandlingUpdateResult>(response);
  }
}

export const feedbackReportService = new FeedbackReportService();
